import type {
  PropertyFilterOperator,
  PropertyFilterOperatorExtended,
  PropertyFilterOption,
  PropertyFilterProperty,
} from '@cloudscape-design/collection-hooks';
import { z } from 'zod';

export const translatedExactFilterOperators = (
  filteringOptions: Omit<PropertyFilterOption, 'propertyKey'>[]
): PropertyFilterOperatorExtended<string>[] =>
  (['=', '!='] as const).map((operator) => ({
    operator,
    format: (value: string) =>
      filteringOptions.find((option) => option.value === value)?.label ?? value,
  }));

export const createIdLabelFieldPropertyFilter = (
  options: { label: string; id: string }[]
): Partial<PropertyFilterProperty> => {
  const getById = (id: string) =>
    options.find((t) => t.id === id)?.label || '-';
  const hasOption = (items: unknown, id: string) =>
    !!((items || []) as { label: string; id: string }[]).find(
      (t) => t.id === id
    );

  const doesNotHaveOption = (items: unknown, id: string) =>
    !hasOption(items, id);

  return {
    operators: [
      ...(['=', ':'] as PropertyFilterOperator[]).map((operator) => ({
        operator,
        format: getById,
        match: hasOption,
      })),
      ...(['!=', '!:'] as PropertyFilterOperator[]).map((operator) => ({
        operator,
        format: getById,
        match: doesNotHaveOption,
      })),
      {
        operator: '<',
        format: (num: string): string => {
          const parsed = z.coerce.number().default(0).safeParse(num);
          if (parsed.success) {
            return parsed.data.toString();
          }

          return '';
        },
        match: (items: unknown, id: string) => {
          return !!((items || []) as { label: string; id: string }[]).find(
            (t) => t.id === id
          );
        },
      },
    ],
  };
};

const COMPARISON_OPERATORS = ['>', '<', '>=', '<='] as const;

// Without a `match` the engine coerces a blank cell to '' and `'' < '5'` is a
// true *string* comparison, leaking every blank row into < and <=.
const comparisonOperators = (
  readValue: (itemValue: unknown) => number | null
): PropertyFilterOperatorExtended<number | string | null>[] =>
  COMPARISON_OPERATORS.map((operator) => ({
    operator,
    match: (itemValue: unknown, tokenValue: number | string | null) => {
      const item = readValue(itemValue);
      // Number('   ') is 0, which would act as a zero threshold.
      const trimmed =
        typeof tokenValue === 'string' ? tokenValue.trim() : tokenValue;
      const token = trimmed === null || trimmed === '' ? null : Number(trimmed);

      if (item === null || token === null || Number.isNaN(token)) {
        return false;
      }

      switch (operator) {
        case '>':
          return item > token;
        case '<':
          return item < token;
        case '>=':
          return item >= token;
        case '<=':
          return item <= token;
      }
    },
  }));

/** Spread alongside the column's existing operators — the list replaces the
 * inherited defaults wholesale, and a dropped operator breaks saved filters. */
export const numericComparisonOperators = comparisonOperators((itemValue) =>
  typeof itemValue === 'number' ? itemValue : null
);

/** For a cell stored pre-formatted as '75%' (attestations `AttestationsCompleted`),
 * which the numeric reader would reject outright. 'N/A' is a blank, not a zero. */
export const percentStringComparisonOperators = comparisonOperators(
  (itemValue) => {
    if (typeof itemValue === 'number') {
      return itemValue;
    }
    if (typeof itemValue !== 'string') {
      return null;
    }
    // Reject an empty remainder: Number('') is 0, i.e. a blank read as a real zero.
    const stripped = itemValue.trim().replace(/%$/, '').trim();
    if (stripped === '') {
      return null;
    }
    const parsed = Number(stripped);

    return Number.isNaN(parsed) ? null : parsed;
  }
);
